(() => {
    let activeAttempt;
    let preparingOptions = false;

    // Remove intents saved by the previous implementation. A later cart update must never resume them.
    try { sessionStorage.removeItem('fiore-buy-now'); } catch (_) {}

    function errorMessage(button, message) {
        const error = button.parentElement.querySelector('[data-fiore-buy-now-error]');
        if (error) {
            error.textContent = message;
            error.hidden = !message;
        }
    }

    function finish(attempt) {
        if (activeAttempt !== attempt) return;
        activeAttempt = null;
        attempt.controller.abort();
        clearTimeout(attempt.timer);
        attempt.cleanups.forEach(cleanup => cleanup());
        attempt.button.disabled = false;
        attempt.button.textContent = attempt.label;
        attempt.button.removeAttribute('aria-busy');
    }

    function checkActive(attempt) {
        if (activeAttempt !== attempt || attempt.controller.signal.aborted) throw new Error('Checkout cancelled');
    }

    async function cartRequest(path, attempt, options = {}) {
        checkActive(attempt);
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}${path}`, {
            ...options,
            headers: { Accept: 'application/json' },
            signal: attempt.controller.signal
        });
        const data = await response.json();
        if (!response.ok || data.status >= 400) throw new Error('Unable to update your cart. Please try again.');
        checkActive(attempt);
        return data;
    }

    function variantQuantity(cart, variantId) {
        return cart.items.reduce((quantity, item) => quantity + (String(item.variant_id) === variantId ? item.quantity : 0), 0);
    }

    function prepareEasify(form, addButton, attempt) {
        return new Promise((resolve, reject) => {
            const signal = attempt.controller.signal;
            const previousNavigationHandler = window.easifyAddToCartEvent;
            // Easify provides this flag for integrations that own the post-add navigation.
            window.easifyAddToCartEvent = true;
            const blockThemeSubmit = event => {
                event.preventDefault();
                event.stopPropagation();
            };
            const prepared = () => resolve(form.dataset.hasAddOnProduct === 'true');
            const aborted = () => reject(new Error('Checkout cancelled'));
            const cleanup = () => {
                document.removeEventListener('tpo_product_added', prepared);
                addButton.removeEventListener('click', blockThemeSubmit);
                signal.removeEventListener('abort', aborted);
                if (previousNavigationHandler === undefined) delete window.easifyAddToCartEvent;
                else window.easifyAddToCartEvent = previousNavigationHandler;
            };
            attempt.cleanups.push(cleanup);
            document.addEventListener('tpo_product_added', prepared, { once: true });
            signal.addEventListener('abort', aborted, { once: true });
            // Let Easify's target handler prepare fields/add-ons, but do not also submit through the theme.
            addButton.addEventListener('click', blockThemeSubmit, { once: true });
            try {
                preparingOptions = true;
                addButton.click();
                if (addButton.dataset.tpoValidate === 'false') reject(new Error('Please check the required customization options.'));
            } catch (error) {
                reject(error);
            } finally {
                preparingOptions = false;
            }
        });
    }

    function waitForRazorpay(attempt) {
        return new Promise((resolve, reject) => {
            const signal = attempt.controller.signal;
            const aborted = () => reject(new Error('Checkout cancelled'));
            signal.addEventListener('abort', aborted, { once: true });
            attempt.cleanups.push(() => signal.removeEventListener('abort', aborted));
            const check = () => {
                if (signal.aborted) return;
                if (typeof window.openRzpCheckout === 'function') resolve(window.openRzpCheckout);
                else setTimeout(check, 200);
            };
            check();
        });
    }

    document.addEventListener('click', async event => {
        const button = event.target.closest('[data-fiore-buy-now]');
        if (!button) {
            // A separate Add to Cart click is not consent to complete an earlier Buy Now attempt.
            if (!preparingOptions && activeAttempt && event.target.closest('[data-btn-addtocart]')) finish(activeAttempt);
            return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (activeAttempt || button.disabled || button.closest('.productView-payment[disabled]')) return;
        const container = button.closest('form');
        const addButton = container?.querySelector('[data-btn-addtocart]') || button.closest('.productView')?.querySelector('[data-btn-addtocart]');
        const form = addButton?.closest('form');
        if (!form || !addButton || addButton.disabled || !form.reportValidity()) return;

        const attempt = { button, label: button.textContent, controller: new AbortController(), cleanups: [] };
        activeAttempt = attempt;
        button.disabled = true;
        button.textContent = 'Opening checkout…';
        button.setAttribute('aria-busy', 'true');
        errorMessage(button, '');
        attempt.timer = setTimeout(() => {
            if (activeAttempt !== attempt) return;
            errorMessage(button, 'Checkout could not start. Please try again or continue from your cart.');
            finish(attempt);
        }, 20000);

        try {
            const selection = new FormData(form);
            const variantId = String(selection.get('id'));
            const quantity = Number(selection.get('quantity') || 1);
            const before = await cartRequest('cart.js', attempt);
            const expectedQuantity = variantQuantity(before, variantId) + quantity;
            // Options apps can replace their button while the cart request is in flight.
            const currentAddButton = form.querySelector('[data-btn-addtocart]');
            if (!currentAddButton || currentAddButton.disabled) throw new Error('Please wait for your product options to load and try again.');
            const addedByEasify = currentAddButton.classList.contains('tpo_add-to-cart')
                ? await prepareEasify(form, currentAddButton, attempt)
                : false;
            checkActive(attempt);
            if (!addedByEasify) {
                // With no paid extras Easify only prepares the form; we must perform the actual POST.
                await cartRequest('cart/add.js', attempt, { method: 'POST', body: new FormData(form) });
            }
            const cart = await cartRequest('cart.js', attempt);
            if (variantQuantity(cart, variantId) < expectedQuantity) throw new Error('Your product was not added. Please check your options and try again.');
            const openCheckout = await waitForRazorpay(attempt);
            checkActive(attempt);
            finish(attempt);
            document.body.classList.remove('cart-sidebar-show', 'add-to-cart-show');
            try {
                openCheckout();
            } catch (_) {
                errorMessage(button, 'Unable to open Razorpay. Please try again or continue from your cart.');
            }
        } catch (error) {
            if (activeAttempt === attempt) {
                errorMessage(button, error.message || 'Unable to start checkout. Please try again.');
                finish(attempt);
            }
        }
    }, true);
})();
